import React, { MutableRefObject, useEffect, useMemo, useState } from 'react';
import { Text, Box, Flex, Divider } from '@televet/kibble-ui';
import RemindersSection from './RemindersSection';
import PostcardsSection from './PostcardsSection';
import useClinicUser from 'shared/hooks/useClinicUser';
import { ClinicPimsIntegrationType, PermissionType } from 'shared/types/graphql';
import { PostcardsOnboardingBanner } from './PostcardsOnboardingBanner';
import { ReminderOnboardingBanner } from './ReminderOnboardingBanner';
import {
  hasInitiatedBitwerxIntegration,
  needsBitwerxIntegration,
} from '../../../../shared/providers/IntegrationsProvider/utils/getBitwerxIntegrationData';
import { RemindersGlobalToggle } from './RemindersGlobalToggle';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { useTracking } from 'shared/providers/TrackingProvider';
import { ServiceReminderEvents } from 'shared/providers/TrackingProvider/EventNames';
import { useTopToolbarContext } from 'shared/components/Layouts/DefaultAuthenticated/components/TopToolbar/context/TopToolbarContext';
import LearnAboutServiceRemindersLink from './LearnAboutServiceRemindersLink';

enum ReminderSection {
  ReminderTypes = 'Reminder Types',
  Postcards = 'Postcards',
}

const ServiceRemindersSettings = ({ viewRef }: { viewRef: MutableRefObject<HTMLDivElement | null> }): JSX.Element => {
  const { userHasPermission, currentClinicId, currentClinic } = useClinicUser();
  const canManagePostcards = userHasPermission(PermissionType.WriteClinic);
  const {
    allIntegrations,
    isPostcardsSupported,
    isServiceRemindersDisabledForEzyvet,
    primaryIntegration,
    primaryIntegrationName,
  } = useIntegrationsProvider();
  const isEzyVet = primaryIntegration?.type === ClinicPimsIntegrationType.Ezyvet;
  const [selectedSection, setSelectedSection] = useState<ReminderSection>(ReminderSection.ReminderTypes);
  const today = new Date();
  const isInTomorrowsChangeRange = today.getHours() < today.getUTCHours();
  const { trackEvent } = useTracking();

  const showBitwerxOnboardingBanner = useMemo(
    () => needsBitwerxIntegration(allIntegrations || []) || hasInitiatedBitwerxIntegration(allIntegrations),
    [allIntegrations],
  );

  const { setTopToolbarAdditionalContent } = useTopToolbarContext();

  useEffect(() => {
    if (isEzyVet && !isServiceRemindersDisabledForEzyvet) {
      setTopToolbarAdditionalContent(<LearnAboutServiceRemindersLink />);
    }

    return (): void => {
      setTopToolbarAdditionalContent(undefined);
    };
  }, [currentClinicId, isEzyVet, isServiceRemindersDisabledForEzyvet, setTopToolbarAdditionalContent]);

  useEffect(() => {
    trackEvent(ServiceReminderEvents.VisitSettingsPage, {
      clinicId: currentClinicId,
      integrationType: primaryIntegration?.type,
      clinicName: currentClinic?.name,
      pims: primaryIntegrationName || '',
    });
  }, [
    currentClinic?.name,
    currentClinicId,
    isEzyVet,
    isServiceRemindersDisabledForEzyvet,
    primaryIntegration?.type,
    primaryIntegrationName,
    trackEvent,
  ]);

  const Tabs = (): JSX.Element => {
    return (
      <>
        <Flex>
          {Object.values(ReminderSection).map((section, index) => {
            const isActive = section === selectedSection;
            const shouldHidePostcards =
              section === ReminderSection.Postcards && (!isPostcardsSupported || !canManagePostcards);
            return (
              <Text
                key={section}
                fontWeight="bold"
                ml={index > 0 ? 6 : 0}
                pb={2}
                cursor="pointer"
                borderBottomWidth={isActive ? '2px' : '0px'}
                borderBottomColor="border.focus"
                onClick={(): void => setSelectedSection(section)}
                hidden={shouldHidePostcards}
              >
                {section}
              </Text>
            );
          })}
        </Flex>
        <Divider />
      </>
    );
  };

  return showBitwerxOnboardingBanner ? (
    <Box>
      <Text variant="subtle" as="p" maxWidth="500px" size="sm" my={5}>
        Remind your clients about upcoming and past due services with tailored email and postcard notifications.
      </Text>
      <ReminderOnboardingBanner />
    </Box>
  ) : (
    <Box>
      <Text variant="subtle" as="p" maxWidth="500px" size="sm" my={5}>
        Remind your clients about upcoming and past due services with tailored email and postcard notifications. The
        changes made right now will take effect {isInTomorrowsChangeRange ? 'tomorrow' : 'in two days'}.
      </Text>

      <RemindersGlobalToggle />

      <Tabs />

      {selectedSection === ReminderSection.Postcards ? (
        <PostcardsSection />
      ) : (
        <>
          {canManagePostcards && (!isEzyVet || isServiceRemindersDisabledForEzyvet) && (
            <PostcardsOnboardingBanner onLearnMore={(): void => setSelectedSection(ReminderSection.Postcards)} />
          )}
          <RemindersSection viewRef={viewRef} />
        </>
      )}
    </Box>
  );
};

export default ServiceRemindersSettings;
